import * as React from "react";
import {
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import EditBookingStyle from "./EditBookingStyle";
import { EditBookingContextProvider } from "./EditBookingContext";
import EditBookingHeader from "./Header/Header";
import Journey from "./Sections/Journey/Journey";
import { Button, Grid } from "@mui/material";
import CustomsInformation from "./Sections/CustomsInformation/CustomsInformation";
import DangerousGoodsForm from "./Sections/DangerousGoods/DangerousGoods";
import UnitId from "./Sections/UnitId/UnitId";
import Loading from "../Loading/Loading";
import { useShallowSelector } from "../../hooks/useShallowSelector";
import RootState from "../../redux/types/rootState";
import { useAppDispatch } from "../../redux/dispatch/useAppDispatch";
import { IBooking } from "../../api/bookings/IBookings";
import { validationSchema } from "./EditBookingValidationSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import { mapBookingToUpdateBooking } from "../../mappers/mapBookingToUpdateBooking";
import { IUpdateBooking } from "../../api/bookings/IUpdate";
import { updateBooking } from "../../redux/slice/bookingSlice";
import { initialUpdateBooking } from "../../utils/factory/IUpdateFactory";
import { useMsal } from "@azure/msal-react";

export interface EditBookingProps {
  isOpen: boolean;
  booking: IBooking;
  onClose: () => void;
}

export default function EditBooking({
  isOpen,
  booking,
  onClose,
}: EditBookingProps) {
  const classes = EditBookingStyle();
  const { accounts } = useMsal();

  const bookingUpdateModel = React.useMemo(() => {
    return booking.id
      ? mapBookingToUpdateBooking(booking)
      : initialUpdateBooking;
  }, [booking]);

  const displayBookingData = React.useMemo(
    () => ({
      referenceCode: booking.referenceCode,
      // globalBookingId : booking.globalBookingId,
    }),
    [booking]
  );

  const methods = useForm<IUpdateBooking>({
    mode: "all",
    defaultValues: bookingUpdateModel,
    resolver: yupResolver(validationSchema),
  });

  const dispatch = useAppDispatch();

  const { handleSubmit, reset, trigger } = methods;

  const isLoading = useShallowSelector(
    (state: RootState) => state.bookings.isLoading
  );

  React.useEffect(() => {
    reset(bookingUpdateModel);
    trigger(); // Trigger validation after resetting the form
  }, [bookingUpdateModel, reset, trigger]);

  const prevIsLoading = React.useRef(isLoading);

  React.useEffect(() => {
    if (prevIsLoading.current && !isLoading) {
      onClose();
    }
    prevIsLoading.current = isLoading;
  }, [isLoading, onClose]);

  type FormSubmitHandler = SubmitHandler<IUpdateBooking>;
  const onSubmitFormValues: FormSubmitHandler = async (
    data: IUpdateBooking,
    e?: React.BaseSyntheticEvent
  ) => {
    e?.preventDefault();
    dispatch(
      updateBooking({
        updateBooking: data,
        bookingId: booking.id,
        userName: accounts[0].name!,
      })
    );
  };

  type FormSubmitErrorHandler = SubmitErrorHandler<IUpdateBooking>;

  const onSubmitError: FormSubmitErrorHandler = async (errors, e) => {
    e?.preventDefault();
  };

  return (
    <FormProvider {...methods}>
      <EditBookingContextProvider
        classes={classes}
        isOpen={isOpen}
        onClose={onClose}
        booking={booking}
      >
        <Dialog
          id="dialogContent-id"
          open={isOpen}
          onClose={onClose}
          maxWidth={"lg"}
        >
          <DialogContent className={classes.container}>
            <form
              id="dialogContent-form-id"
              onSubmit={handleSubmit(onSubmitFormValues, onSubmitError)}
            >
              {isLoading ? <Loading parentId="dialogContent-form-id" /> : null}

              <EditBookingHeader />

              <Journey referenceCode={displayBookingData.referenceCode ?? ""} />

              <Divider className={classes.dividers} />

              <UnitId classes={classes} />

              <Divider className={classes.dividers} />

              <CustomsInformation classes={classes} />

              <Divider className={classes.dividers} />

              <DangerousGoodsForm classes={classes} />

              <Grid justifyContent={"flex-end"} container xs={12} md={12} item>
                <Button
                  variant="text"
                  className={classes.cancelButton + " " + classes.buttons}
                  onClick={onClose}
                >
                  CANCEL
                </Button>

                <Button
                  variant="text"
                  type="submit"
                  className={classes.okButton + " " + classes.buttons}
                >
                  SAVE
                </Button>
              </Grid>
            </form>
          </DialogContent>
        </Dialog>
      </EditBookingContextProvider>
    </FormProvider>
  );
}
